import { effect, inject, Injectable, untracked } from '@angular/core';

import { ListingSelectionStore } from '@features/search/services/listing-selection.store';

import { CHATBOT_CONTEXT_IDS } from '../constants/chatbot-contexts';
import { ChatbotContextService } from './chatbot-context';

@Injectable({
    providedIn: 'root',
})
export class ChatbotSelectedListingsContextService {
    private readonly contextService = inject(ChatbotContextService);
    private readonly listingSelectionStore = inject(ListingSelectionStore);

    private readonly selectedListings = this.listingSelectionStore.selectedListings.selectedItems;
    private readonly selectedCount = this.listingSelectionStore.selectedListings.count;

    initialize(): void {
        effect(() => {
            const listings = this.selectedListings();
            const count = this.selectedCount();

            untracked(() => {
                if (count === 0) {
                    this.contextService.remove(CHATBOT_CONTEXT_IDS.SELECTED_LISTINGS);

                    return;
                }

                const listingDetails = listings.map(listing => {
                    const parts: string[] = [];

                    if (listing.address) {
                        parts.push(listing.address);
                    }

                    if (listing.price) {
                        parts.push(`$${listing.price.toLocaleString()}`);
                    }

                    if (listing.bedrooms) {
                        parts.push(`${listing.bedrooms} bed`);
                    }

                    if (listing.bathrooms) {
                        parts.push(`${listing.bathrooms} bath`);
                    }

                    if (listing.squareFootage) {
                        parts.push(`${listing.squareFootage} sqft`);
                    }

                    return parts.join(', ');
                }).join('\n');

                const message = `User has ${count} listing${count > 1 ? 's' : ''} selected:\n${listingDetails}`;

                const data = listings.reduce((acc, listing, index) => {
                    acc[`listing_${index + 1}_id`] = listing.id.toString();

                    if (listing.address) {
                        acc[`listing_${index + 1}_address`] = listing.address;
                    }

                    if (listing.price) {
                        acc[`listing_${index + 1}_price`] = listing.price.toString();
                    }

                    return acc;
                }, {} as Record<string, string>);

                this.contextService.set(
                    CHATBOT_CONTEXT_IDS.SELECTED_LISTINGS,
                    'Selected Listings',
                    message,
                    data
                );
            });
        });
    }
}
